import { useEffect, useState } from "react";
import {
  Box,
  Text,
  VStack,
  Flex,
  IconButton,
  Spinner,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FaFileAlt, FaImage, FaVideo, FaFilePdf } from "react-icons/fa";
import axios from "../../axiosConfig";

const fileIconFor = (type) => {
  if (!type) return <FaFileAlt />;
  if (type.startsWith("image")) return <FaImage />;
  if (type.startsWith("video")) return <FaVideo />;
  if (type === "application/pdf") return <FaFilePdf />;
  return <FaFileAlt />;
};

// ✅ Robust S3 key extraction (no URL normalization bugs)
const extractKeyFromS3Url = (url) => {
  if (!url) return null;

  const clean = url.split("?")[0];
  const marker = ".amazonaws.com/";
  const idx = clean.indexOf(marker);

  let key;
  if (idx !== -1) {
    key = clean.substring(idx + marker.length);
  } else {
    try {
      key = new URL(clean).pathname.replace(/^\//, "");
    } catch {
      key = clean.replace(/^\//, "");
    }
  }

  return decodeURIComponent(key);
};

const WidgetAttachmentsList = ({ widgetId, refreshKey }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeAttachment, setActiveAttachment] = useState(null);
  const [signedPreviewUrl, setSignedPreviewUrl] = useState(null);
  const [signedDownloadUrl, setSignedDownloadUrl] = useState(null);

  const [attachmentToDelete, setAttachmentToDelete] = useState(null);

  const [editingAttachment, setEditingAttachment] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editError, setEditError] = useState("");

  const previewModal = useDisclosure();
  const deleteModal = useDisclosure();
  const editModal = useDisclosure();
  const toast = useToast();

  const fetchAttachments = async () => {
    try {
      const res = await axios.get(`/widgets/${widgetId}/widget_attachments`);
      setAttachments(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetId, refreshKey]);

  const confirmDelete = (attachment) => {
    setAttachmentToDelete(attachment);
    deleteModal.onOpen();
  };

  const performDelete = async () => {
    if (!attachmentToDelete) return;

    await axios.delete(
      `/widgets/${widgetId}/widget_attachments/${attachmentToDelete.id}`
    );

    deleteModal.onClose();
    setAttachmentToDelete(null);
    fetchAttachments();
  };

  const openPreview = async (attachment) => {
    setActiveAttachment(attachment);
    setSignedPreviewUrl(null);
    setSignedDownloadUrl(null);
    previewModal.onOpen();

    try {
      const key = extractKeyFromS3Url(attachment.s3_url);

      const previewRes = await axios.post("/uploads/presign_get", { key });
      setSignedPreviewUrl(previewRes.data.url);

      const downloadRes = await axios.post("/uploads/presign_download", { key });
      setSignedDownloadUrl(downloadRes.data.url);
    } catch (err) {
      console.error("Failed to load signed URLs", err);
    }
  };

  const startEdit = (attachment) => {
    setEditingAttachment(attachment);
    setEditTitle(attachment.title || attachment.file_name || "");
    setEditError("");
    editModal.onOpen();
  };

  const saveEdit = async () => {
    if (!editTitle.trim()) {
      setEditError("Title can't be blank");
      return;
    }

    try {
      await axios.put(
        `/widgets/${widgetId}/widget_attachments/${editingAttachment.id}`,
        {
          widget_attachment: { title: editTitle.trim() },
        }
      );

      editModal.onClose();
      setEditingAttachment(null);
      fetchAttachments();

      toast({
        title: "Resource updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Failed to update attachment", err);
      setEditError("Unable to save changes");
    }
  };

  if (loading) return <Spinner />;

  return (
    <>
      {/* RESOURCE LIST */}
      <VStack align="stretch" spacing={3}>
        {attachments.map((a) => (
          <Flex
            key={a.id}
            justify="space-between"
            align="center"
            borderWidth="1px"
            p={3}
            borderRadius="md"
          >
            <Flex align="center" gap={3}>
              {fileIconFor(a.file_type)}
              <Box>
                <Text fontWeight="bold">{a.title}</Text>
                <Text fontSize="xs" color="gray.500">
                  {a.file_name}
                </Text>

                <Link
                  onClick={() => openPreview(a)}
                  color="orange.500"
                  fontSize="sm"
                >
                  Preview
                </Link>
              </Box>
            </Flex>

            <Flex gap={2}>
              <IconButton
                icon={<EditIcon />}
                aria-label="Edit resource title"
                size="sm"
                onClick={() => startEdit(a)}
              />
              <IconButton
                icon={<DeleteIcon />}
                colorScheme="red"
                size="sm"
                aria-label="Delete resource"
                onClick={() => confirmDelete(a)}
              />
            </Flex>
          </Flex>
        ))}
      </VStack>

      {/* PREVIEW MODAL */}
      <Modal isOpen={previewModal.isOpen} onClose={previewModal.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{activeAttachment?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody minH="400px">
            {!signedPreviewUrl && <Spinner />}

            {signedPreviewUrl && activeAttachment?.file_type?.startsWith("image") && (
              <img
                src={signedPreviewUrl}
                alt={activeAttachment.title}
                style={{ maxWidth: "100%", borderRadius: "6px" }}
              />
            )}

            {signedPreviewUrl && activeAttachment?.file_type?.startsWith("video") && (
              <video controls style={{ width: "100%" }}>
                <source src={signedPreviewUrl} type={activeAttachment.file_type} />
              </video>
            )}

            {signedPreviewUrl && activeAttachment?.file_type === "application/pdf" && (
              <iframe
                src={signedPreviewUrl}
                title="PDF Preview"
                style={{
                  width: "100%",
                  height: "80vh",
                  border: "none",
                  borderRadius: "6px",
                }}
              />
            )}

            {signedPreviewUrl &&
              !activeAttachment?.file_type?.startsWith("image") &&
              !activeAttachment?.file_type?.startsWith("video") &&
              activeAttachment?.file_type !== "application/pdf" && (
                <Link href={signedPreviewUrl} isExternal color="orange.500">
                  Open file in new tab
                </Link>
              )}
          </ModalBody>

          <ModalFooter>
            <Button
              as="a"
              href={signedDownloadUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              colorScheme="orange"
              mr={3}
              isDisabled={!signedDownloadUrl}
            >
              Download
            </Button>

            <Button onClick={previewModal.onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* DELETE CONFIRMATION */}
      <Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Resource</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete{" "}
            <strong>{attachmentToDelete?.title}</strong>?
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={performDelete}>
              Yes, delete
            </Button>
            <Button onClick={deleteModal.onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* EDIT TITLE MODAL */}
      <Modal isOpen={editModal.isOpen} onClose={editModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Resource Title</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={!!editError}>
              <FormLabel>Title</FormLabel>
              <Input
                value={editTitle}
                onChange={(e) => {
                  setEditTitle(e.target.value);
                  setEditError("");
                }}
                placeholder="Enter a descriptive title"
              />
              {editError && <FormErrorMessage>{editError}</FormErrorMessage>}
            </FormControl>

            {editingAttachment && (
              <Box mt={3}>
                <Text fontSize="xs" color="gray.500">
                  File: {editingAttachment.file_name}
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="orange" mr={3} onClick={saveEdit}>
              Save
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                editModal.onClose();
                setEditingAttachment(null);
                setEditError("");
              }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default WidgetAttachmentsList;